import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of, from } from "rxjs";
import { switchMap, map, catchError, exhaustMap, shareReplay, pluck, mergeMap, takeUntil } from "rxjs/operators";
import { isDisabledVip } from "@nxplatform/customer-noauth/slices/customer-noauth.slice";
import { widgetMinimized } from "@nxplatform/helpers/constants/customer";
import { CompPointTypesNames } from "@nxplatform/helpers/entities/common-entities";
import { UserGroupTypes } from "@nxplatform/platform-entities/customer/customer-enum-types";
import { SVGIcons } from "@nxplatform/platform-entities/icons";
import { selectResponsibleGamblingTools, selectIsReferralProgramEnabled } from "@nxplatform/platform-settings/brand-settings.slice";
import { allowedLinks, excludedCasinoLinks, excludedLimits } from "@nxplatform/platform-settings/locale-setup.routes";
import { getLocaleSetupState, isDisabledPromotions, selectAdditionalCashierLink, selectIsNewAnnualReport, selectGrpcIsActive } from "@nxplatform/platform-settings/locale-setup.selectors";
import { setCustomerDisabledStatusCode, customerWSTokenStart, getCustomerStart, getCustomerLastBetStart, getCustomerFastAmountsStart, customerSocketOpenSuccess, changeBalanceAccountSuccess, customerUpdateSuccess, notifyAboutDeposit, updateWidgetMaximizedState, getCustomerAccountBalance, customerDepositError, submitCashierForm, changeBonusIssuanceStart, changeCustomerBonusIssuance, clearCustomerData, customerUpdateStart, updateCustomerEntityBonusType, updateUserAccountSmarticoHashStart, clearWebsocketHandledNotifications } from "@nxplatform/store-shared/customer/shared-customer-actions";
import { CUSTOMER_FEATURE_KEY } from "@nxplatform/store-shared/customer/shared-customer-feature-keys";
import { getCustomerState, selectCustomerEntities, selectCustomerTokenEntities, selectCustomerId } from "@nxplatform/store-shared/customer/shared-customer-selectors";
import { getPlatformSettingsState } from "@nxplatform/store-shared/platform-settings/shared-platform-settings-selectors";
import { AppStorage } from "@service/api-client/http/helpers/AppStorage";
import { __ } from "@service/api-client/http/helpers/translates";
import { DateSettingsFormat } from "@service/api-client/utils/date-settings";
import { dateTimeConverter } from "@service/api-client/utils/date-time.converter";
import { selectisBlockedVip, selectIntegrations, selectLimitsSelfExclusionEnabled, selectLimitsSelfDeletionEnabled } from "@nxplatform/platform-settings/platform-settings.selectors";
import { WSNamespaces } from "@service/api-client/websockets/ws-client";
import { fromWebSocket } from "@service/api-client/websockets/centrifuge";
var ReactivationSent = /* @__PURE__ */ ((ReactivationSent2) => {
  ReactivationSent2[ReactivationSent2["notSent"] = 0] = "notSent";
  ReactivationSent2[ReactivationSent2["sent"] = 1] = "sent";
  ReactivationSent2[ReactivationSent2["resent"] = 2] = "resent";
  return ReactivationSent2;
})(ReactivationSent || {});
const defaultCompPoints = {
  status: 0,
  redeemable: {
    amount: 0,
    rates: {}
  }
};
const initialCustomerState = {
  entities: {
    smarticoUserIdPrefix: null,
    disabledStatusCode: null,
    ondatoVerificationAvailable: false,
    ondatoVerificationNotice: false,
    allowedFeatures: {
      isCryptoEnabled: false,
      smartico: true
    }
  },
  loaded: false,
  loadedOnce: false,
  forgotLoaded: false,
  forgotMessage: null,
  error: null,
  passwordError: null,
  passwordLoaded: false,
  update: false,
  updateLoading: false,
  gviveMigrationCompleted: false,
  casinoCompPoints: defaultCompPoints,
  sportCompPoints: defaultCompPoints,
  compPointCasinoResetAt: null,
  compPointSportResetAt: null,
  customerSessionLimitOver: false,
  customerBalanceLimitOver: false,
  personalCodeCheckSuccess: null,
  personalCheckEntity: null,
  personalCodeCheckFailure: null,
  wsToken: null,
  widgetsMaximized: !!AppStorage.get(widgetMinimized),
  customerGamblingAccount: null,
  firstDepositBonusCanceledByUser: false,
  accountChanging: false,
  showOntarioMigrationNotice: false,
  lastBetSums: {
    sums: {}
  },
  fastAmounts: {
    fastAmounts: {}
  },
  respnsibleGamblingToolsError: null,
  respnsibleGamblingToolsActive: false,
  watchersChatPlatformConfig: {
    apiKey: null,
    roomId: null,
    roomTitle: null,
    userId: null,
    lastMessageDomain: null,
    chatDomain: null
  },
  forceHidePopupWithLimits: false,
  broadcastAllowed: false,
  handledNotifications: [],
  smarticoHashLoading: false
};
const customerSlice = createSlice({
  name: CUSTOMER_FEATURE_KEY,
  initialState: initialCustomerState,
  reducers: {
    changePasswordStart: (state, action) => {
      state.passwordLoaded = false;
    },
    changePasswordSuccess: (state, action) => {
      state.passwordLoaded = true;
    },
    changePasswordFailed: (state, action) => {
      state.passwordError = action.payload;
    },
    getBroadcastStart: () => {
    },
    getBroadcastStartSuccess: (state, action) => {
    },
    getBroadcastStartFailure: (state, action) => {
    },
    getBroadcastStop: (state, action) => {
    },
    getBroadcastStopSuccess: (state, action) => {
    },
    getBroadcastStopFailure: (state, action) => {
    },
    stopBroadcastTrialWS: (state) => {
      state.broadcastAllowed = false;
    },
    allowBroadcast: (state) => {
      state.broadcastAllowed = true;
    },
    getCustomerSuccess: (state, action) => {
      state.loaded = true;
      state.loadedOnce = true;
      const currentEntities = JSON.stringify(state.entities);
      const incomeEntities = JSON.stringify(action.payload);
      if (currentEntities.localeCompare(incomeEntities) !== 0) {
        const { is_session_limit_hit } = action.payload;
        const { is_balance_limit_hit } = action.payload;
        state.entities = action.payload;
        state.customerSessionLimitOver = is_session_limit_hit || false;
        state.customerBalanceLimitOver = is_balance_limit_hit || false;
        state.broadcastAllowed = action.payload.broadcastAllowed;
        state.casinoCompPoints = {
          ...defaultCompPoints,
          ...action.payload.compPointAccounts[CompPointTypesNames.casino] || {}
        };
        state.sportCompPoints = {
          ...defaultCompPoints,
          ...action.payload.compPointAccounts[CompPointTypesNames.sport] || {}
        };
        state.compPointCasinoResetAt = action.payload.compPointCasinoResetAt;
        state.compPointSportResetAt = action.payload.compPointSportResetAt;
      }
    },
    updateCustomerAccount: (state, action) => {
      const {
        accountId,
        amount,
        availableForWithdrawal,
        casinoBonusAmount,
        sportsBonusSum,
        generalBonusSum
      } = action.payload;
      state.entities.accounts = state.entities.accounts.map((acc) => {
        if (Number(acc.id) === accountId) {
          return {
            ...acc,
            amount,
            availableForWithdrawal,
            casinoBonusAmount,
            sportsBonusSum,
            generalBonusSum
          };
        }
        return acc;
      });
    },
    getCustomerFailure: (state, action) => {
      state.error = action.payload;
    },
    getCustomerLastBetSuccess: (state, action) => {
      state.loaded = true;
      state.lastBetSums = action.payload;
    },
    getCustomerLastBetFailure: (state, action) => {
      state.error = action.payload;
    },
    getCustomerFastAmountsSuccess: (state, action) => {
      state.loaded = true;
      state.fastAmounts = action.payload;
    },
    getCustomerFastAmountsFailure: (state, action) => {
      state.error = action.payload;
    },
    customerUpdateFail: (state, action) => {
      state.updateLoading = false;
    },
    updateMigratedCustomerStart: (state, action) => {
      state.gviveMigrationCompleted = false;
    },
    updateMigratedCustomerError: (state, action) => {
    },
    updateMigratedCustomerSuccess: (state, action) => {
      state.gviveMigrationCompleted = true;
    },
    changeBonusIssuanceSuccess: (state, action) => {
    },
    changeBonusIssuanceFail: () => {
    },
    customerSocketError: (state, action) => {
    },
    clearPasswordLoadedState: (state) => {
      state.passwordLoaded = initialCustomerState.passwordLoaded;
    },
    customerPersonalCheckStart: (state) => {
      state.personalCheckEntity = null;
      state.personalCodeCheckSuccess = null;
      state.personalCodeCheckFailure = null;
    },
    customerPersonalCheckSuccess: (state, action) => {
      state.personalCodeCheckSuccess = true;
      state.personalCheckEntity = action.payload;
    },
    customerPersonalCheckFailure: (state, action) => {
      state.personalCodeCheckFailure = true;
    },
    getCustomerGamblingInfoStart: (state) => {
    },
    getCustomerGamblingInfoSuccess: (state, action) => {
      state.customerGamblingAccount = action.payload;
    },
    customerWSTokenFail: (state) => {
    },
    getCustomerGamblingInfoFail: (state) => {
    },
    customerWSOpenStart: (state) => {
    },
    customerWSTokenSuccess: (state, action) => {
      state.wsToken = action.payload.token;
    },
    createBalanceAccountStart: (state, { payload }) => {
      if (payload.change) state.accountChanging = true;
    },
    createBalanceAccountSuccess: (state) => {
      state.accountChanging = false;
    },
    createBalanceAccountError: (state) => {
      state.accountChanging = false;
    },
    changeBalanceAccountStart: (state) => {
      state.accountChanging = true;
    },
    changeBalanceAccountError: (state) => {
      state.accountChanging = false;
    },
    changeMainCurrencyStart: (state) => {
      state.accountChanging = true;
    },
    updateCustomerState: (state, action) => {
      const {
        userRegistrationCompleted,
        userActivated,
        userVerified,
        hasFirstDeposit,
        ondatoVerificationAvailable,
        ondatoVerificationRequired,
        ondatoVerificationNotice
      } = action.payload || {};
      state.entities.isNeedCompleteRegistration = !userRegistrationCompleted;
      state.entities.isActivated = userActivated ?? state.entities.isActivated;
      state.entities.isVerified = Number(userVerified) ?? state.entities.isVerified;
      state.entities.first_deposit = hasFirstDeposit ?? state.entities.first_deposit;
      state.entities.ondatoVerificationAvailable = ondatoVerificationAvailable;
      state.entities.ondatoVerificationRequired = ondatoVerificationRequired;
      state.entities.ondatoVerificationNotice = ondatoVerificationNotice;
    },
    updateCustomerLimitsHit: (state, action) => {
      const { sessionLimitReached, balanceLimitReached } = action.payload;
      state.entities.is_balance_limit_hit = balanceLimitReached ?? state.entities.is_balance_limit_hit;
      state.entities.is_session_limit_hit = sessionLimitReached ?? state.entities.is_session_limit_hit;
    },
    updateCustomerCompPoints: (state, action) => {
      const { compPointAccounts, compPointCasinoResetAt, compPointSportResetAt } = action.payload;
      state.entities.compPointAccounts = compPointAccounts ?? state.entities.compPointAccounts;
      state.entities.compPointCasinoResetAt = compPointCasinoResetAt ?? state.entities.compPointCasinoResetAt;
      state.entities.compPointSportResetAt = compPointSportResetAt ?? state.entities.compPointSportResetAt;
    },
    updateGBGUserResult: (state, action) => {
      state.entities.gbg = action.payload;
    },
    changeOntarioMigrationNoticeState: (state, action) => {
      state.showOntarioMigrationNotice = action.payload;
    },
    subscribeLastBetUpdateWsStart: (state, action) => {
    },
    lastBetUpdateSocketOpenSuccess: (state, action) => {
    },
    updateLastSumBet: (state, action) => {
      state.lastBetSums = action.payload;
    },
    clearGviveMigrationCompletion: (state) => {
      state.gviveMigrationCompleted = false;
    },
    setResponsibleGamblingToolsError: (state, action) => {
      state.respnsibleGamblingToolsError = action.payload;
    },
    setResponsibleGamblingToolsActive: (state, action) => {
      state.respnsibleGamblingToolsActive = action.payload;
    },
    watchersChatStart: () => {
    },
    watchersChatSuccess: (state, action) => {
      state.watchersChatPlatformConfig = action.payload;
    },
    watchersChatFail: () => {
    },
    hideCustomerLimitsSetupPopup: (state) => {
      state.entities = {
        ...state.entities,
        isNeedToShowPopupWithLimits: false
      };
      state.forceHidePopupWithLimits = true;
    },
    toggleBonusInCashierStart: (state, action) => {
      state.entities = {
        ...state.entities,
        isBonusOnInCashier: action.payload
      };
    },
    toggleBonusInCashierSuccess: (state, action) => {
    },
    toggleBonusInCashierFail: () => {
    },
    setWebsocketHandledNotifications: (state, action) => {
      state.handledNotifications = [...state.handledNotifications, ...action.payload];
    },
    updateUserAccountSmarticoHashSuccess: (state, action) => {
      state.entities.accounts = state.entities.accounts.map((acc) => {
        if (acc.is_main) {
          return {
            ...acc,
            smarticoUserHash: action.payload.smarticoUserHash
          };
        }
        return acc;
      });
      state.smarticoHashLoading = false;
    },
    updateUserAccountSmarticoHashError: (state) => {
      state.smarticoHashLoading = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(setCustomerDisabledStatusCode, (state, action) => {
      state.entities.disabledStatusCode = action.payload.disabledStatusCode;
    });
    builder.addCase(customerWSTokenStart, () => {
    });
    builder.addCase(getCustomerStart, (state, action) => {
      state.loaded = false;
    });
    builder.addCase(getCustomerLastBetStart, (state, action) => {
      state.loaded = false;
    });
    builder.addCase(getCustomerFastAmountsStart, (state, action) => {
      state.loaded = false;
    });
    builder.addCase(customerSocketOpenSuccess, (state, action) => {
    });
    builder.addCase(changeBalanceAccountSuccess, (state) => {
      state.accountChanging = false;
    });
    builder.addCase(customerUpdateSuccess, (state, action) => {
      state.update = true;
      state.updateLoading = false;
    });
    builder.addCase(notifyAboutDeposit, (state, action) => {
    });
    builder.addCase(updateWidgetMaximizedState, (state, action) => {
      state.widgetsMaximized = action.payload;
      action.payload ? AppStorage.set(widgetMinimized, 1) : AppStorage.remove(widgetMinimized);
    });
    builder.addCase(getCustomerAccountBalance, (state, action) => {
    });
    builder.addCase(customerDepositError, (state, action) => {
    });
    builder.addCase(submitCashierForm, (state, action) => {
    });
    builder.addCase(changeBonusIssuanceStart, () => {
    });
    builder.addCase(changeCustomerBonusIssuance, (state, action) => {
      state.entities = { ...state.entities, ...action.payload };
    });
    builder.addCase(clearCustomerData, (state, action) => {
      state.entities = initialCustomerState.entities;
      state.casinoCompPoints = defaultCompPoints;
      state.sportCompPoints = defaultCompPoints;
      state.loaded = initialCustomerState.loaded;
      state.forgotLoaded = initialCustomerState.forgotLoaded;
      state.error = initialCustomerState.error;
      state.passwordLoaded = initialCustomerState.passwordLoaded;
      state.update = initialCustomerState.update;
      state.loadedOnce = false;
      state.respnsibleGamblingToolsError = initialCustomerState.respnsibleGamblingToolsError;
      state.forceHidePopupWithLimits = initialCustomerState.forceHidePopupWithLimits;
    });
    builder.addCase(customerUpdateStart, (state, action) => {
      state.update = false;
      state.updateLoading = true;
    });
    builder.addCase(updateCustomerEntityBonusType, (state, action) => {
      state.entities.bonusType = action.payload.bonusType;
      state.firstDepositBonusCanceledByUser = action.payload.bonusType === null;
    });
    builder.addCase(updateUserAccountSmarticoHashStart, (state) => {
      state.smarticoHashLoading = true;
    });
    builder.addCase(clearWebsocketHandledNotifications, (state) => {
      state.handledNotifications = [];
    });
  }
});
const customerReducer = customerSlice.reducer;
const {
  getCustomerSuccess,
  getCustomerFailure,
  getBroadcastStart,
  getBroadcastStartSuccess,
  getBroadcastStartFailure,
  getBroadcastStop,
  getBroadcastStopSuccess,
  getBroadcastStopFailure,
  stopBroadcastTrialWS,
  allowBroadcast,
  changePasswordStart,
  changePasswordSuccess,
  changePasswordFailed,
  customerUpdateFail,
  updateMigratedCustomerStart,
  updateMigratedCustomerError,
  updateMigratedCustomerSuccess,
  changeBonusIssuanceSuccess,
  changeBonusIssuanceFail,
  customerSocketError,
  clearPasswordLoadedState,
  customerPersonalCheckFailure,
  customerPersonalCheckStart,
  customerPersonalCheckSuccess,
  getCustomerGamblingInfoStart,
  getCustomerGamblingInfoSuccess,
  getCustomerGamblingInfoFail,
  customerWSOpenStart,
  customerWSTokenSuccess,
  customerWSTokenFail,
  changeBalanceAccountStart,
  changeBalanceAccountError,
  updateCustomerState,
  updateCustomerLimitsHit,
  updateCustomerCompPoints,
  updateGBGUserResult,
  updateCustomerAccount,
  changeOntarioMigrationNoticeState,
  getCustomerLastBetSuccess,
  getCustomerLastBetFailure,
  getCustomerFastAmountsSuccess,
  getCustomerFastAmountsFailure,
  subscribeLastBetUpdateWsStart,
  lastBetUpdateSocketOpenSuccess,
  updateLastSumBet,
  clearGviveMigrationCompletion,
  setResponsibleGamblingToolsError,
  setResponsibleGamblingToolsActive,
  watchersChatStart,
  watchersChatSuccess,
  watchersChatFail,
  hideCustomerLimitsSetupPopup,
  createBalanceAccountStart,
  createBalanceAccountSuccess,
  createBalanceAccountError,
  changeMainCurrencyStart,
  toggleBonusInCashierStart,
  toggleBonusInCashierSuccess,
  toggleBonusInCashierFail,
  setWebsocketHandledNotifications,
  updateUserAccountSmarticoHashSuccess,
  updateUserAccountSmarticoHashError
} = customerSlice.actions;
const createYearList = createSelector(getCustomerState, (s) => {
  const thisYear = Number(
    dateTimeConverter(/* @__PURE__ */ new Date(), DateSettingsFormat.onlyYear)
  );
  const regYear = Number(
    dateTimeConverter(s.entities.createdAt, DateSettingsFormat.onlyYear)
  );
  const yearList = [];
  for (let i = regYear; i < thisYear + 1; i++) {
    yearList.push({ name: `${i}`, id: i });
  }
  return yearList;
});
const selectCustomerLoadedOnce = createSelector(
  getCustomerState,
  (s) => s.loadedOnce
);
const selectCustomerLoaded = createSelector(
  getCustomerState,
  (s) => s.loaded
);
const selectCustomerError = createSelector(
  getCustomerState,
  (s) => s.error
);
const selectCustomerGamblingInfo = createSelector(
  getCustomerState,
  (s) => s.customerGamblingAccount
);
const selectCustomerAccounts = createSelector(getCustomerState, (s) => {
  var _a;
  return (((_a = s.entities) == null ? void 0 : _a.accounts) || []).filter(
    (acc) => {
      var _a2;
      return acc.currency !== ((_a2 = s.entities) == null ? void 0 : _a2.currency);
    }
  );
});
const selectAccountsForDropDown = createSelector(
  getCustomerState,
  (s) => {
    var _a, _b, _c;
    if (((_a = s.entities) == null ? void 0 : _a.accounts) && ((_c = (_b = s.entities) == null ? void 0 : _b.accounts) == null ? void 0 : _c.length) > 0) {
      return s.entities.accounts.map((account) => ({
        icon: account.currency,
        id: account.id,
        name: account.currency_name,
        amount: account.amount
      }));
    }
    return [];
  }
);
const selectCustomerEntitiesBonusType = createSelector(
  getCustomerState,
  (s) => {
    var _a;
    return s.firstDepositBonusCanceledByUser ? null : (_a = s.entities) == null ? void 0 : _a.bonusType;
  }
);
const selectCustomerVipNotAllowed = createSelector(
  selectCustomerEntities,
  (customer) => customer.is_vip_forbidden
);
const selectCustomerUpdateLoadingState = createSelector(
  getCustomerState,
  (s) => s.updateLoading
);
const selectIsGviveMigrationCompleted = createSelector(
  getCustomerState,
  (s) => s.gviveMigrationCompleted
);
const selectCustomerSessionLimitState = createSelector(
  getCustomerState,
  (s) => s.customerSessionLimitOver
);
const selectCustomerBalanceLimitState = createSelector(
  getCustomerState,
  (s) => s.customerBalanceLimitOver
);
const selectCustomerPasswordLoadedStatus = createSelector(
  getCustomerState,
  (s) => s.passwordLoaded
);
const selectCustomerCompPointsCasino = createSelector(
  getCustomerState,
  (s) => s.casinoCompPoints
);
const selectCustomerPersonalCodeCheck = createSelector(
  getCustomerState,
  (s) => s.personalCheckEntity
);
const selectCustomerWsToken = createSelector(
  getCustomerState,
  (s) => s.wsToken
);
const selectBalanceAccountCurrentlyChanging = createSelector(
  getCustomerState,
  (s) => s.accountChanging
);
const selectWatchersChatPlatformConfig = createSelector(
  getCustomerState,
  (s) => s.watchersChatPlatformConfig
);
const selectIsOndatoVerifiedCustomer = createSelector(
  getCustomerState,
  ({ entities: { groups } = { groups: [] } }) => !!groups && !!groups.find((group) => group.internalId === UserGroupTypes.ONDATO_VERIFIED)
);
const selectOntarioMigrationNoticeState = createSelector(
  getCustomerState,
  (s) => s.showOntarioMigrationNotice
);
const selectCustomerCustomerHash = createSelector(
  getCustomerState,
  (s) => s.entities.customerHash
);
const selectCustomerToken = createSelector(
  getCustomerState,
  ({ entities = {} }) => entities.customerToken
);
const selectCustomerDeductibleStake = createSelector(
  getCustomerState,
  ({ entities = {} }) => entities.deductibleStake
);
const getCustomerFinalizeRegStep = createSelector(
  getCustomerState,
  ({ entities = {} }) => entities.finalizeRegistrationData
);
const selectCustomerGroups = createSelector(
  getCustomerState,
  ({ entities = {} }) => entities.groups
);
const selectIsCustomerYoungAdult = createSelector(
  getCustomerState,
  ({ entities = {} }) => {
    var _a;
    return (_a = entities.groups) == null ? void 0 : _a.some((group) => group.internalId === UserGroupTypes.YOUNG_ADULT);
  }
);
const selectIsCustomerGviveVerified = createSelector(
  getCustomerState,
  ({ entities = {} }) => {
    var _a;
    return (_a = entities.groups) == null ? void 0 : _a.some((group) => group.internalId === UserGroupTypes.GVIVE_VERIFIED);
  }
);
const getForceUserLimitsSetup = createSelector(
  getCustomerState,
  (s) => !s.forceHidePopupWithLimits && !!s.entities.isNeedToShowPopupWithLimits
);
const selectResponsibleGamblingToolsError = createSelector(
  getCustomerState,
  (s) => s.respnsibleGamblingToolsError
);
const selectResponsibleGamblingToolsActive = createSelector(
  getCustomerState,
  (s) => s.respnsibleGamblingToolsActive
);
const selectCustomerBonusOnInCashier = createSelector(
  getCustomerState,
  ({ entities = {} }) => {
    return entities.isBonusOnInCashier;
  }
);
const showVerificationPageAfterRegistration = createSelector(
  getCustomerState,
  ({ entities = {} }) => entities.redirectToVerificationPageAfterRegistration
);
const selectCustomerVerificationOndatoStatus = createSelector(
  getCustomerState,
  ({ entities = {} }) => entities.ondatoVerificationAvailable || false
);
const selectIsOndatoVerificationRequired = createSelector(
  getCustomerState,
  ({ entities = {} }) => entities.ondatoVerificationRequired || false
);
const selectCustomerOndatoVerificationNoticeStatus = createSelector(
  getCustomerState,
  ({ entities = {} }) => entities.ondatoVerificationNotice || false
);
const selectIsPgwWidget = createSelector(
  [getPlatformSettingsState, getCustomerState],
  (s, customer) => {
    var _a, _b;
    const pgwCountries = s.pgw.widget_countries;
    const customerCountry = (_b = (_a = customer.entities) == null ? void 0 : _a.country) == null ? void 0 : _b.code;
    return pgwCountries.includes(customerCountry);
  }
);
const selectLocaleSetupCabinetMenu = createSelector(
  [
    getLocaleSetupState,
    getPlatformSettingsState,
    isDisabledVip,
    isDisabledPromotions,
    selectisBlockedVip,
    selectIntegrations,
    selectResponsibleGamblingTools,
    selectResponsibleGamblingToolsActive,
    selectLimitsSelfExclusionEnabled,
    selectLimitsSelfDeletionEnabled,
    selectAdditionalCashierLink,
    selectIsNewAnnualReport,
    selectIsReferralProgramEnabled
  ],
  (s, platformSettings, disabledVip, disabledPromotions, isBlockedVipFrontSettings, integrations, responsibleGamblingTools, newLimitsEnabled, selfExclusionEnabled, selfDeletionEnabled, additionalCashierLink, newAnnualReport, brandRefProgramEnabled) => {
    var _a;
    const { neccton } = integrations || {};
    const { limits, refProgramEnabled: platformRefProgramEnabled, disableSportsBetting } = platformSettings || {};
    let cabinetMenu = [...s.entity.cabinetMenu];
    const refProgramEnabled = platformRefProgramEnabled && brandRefProgramEnabled;
    const {
      limits: defaultLimits,
      selfExclusion,
      accountDeactivation,
      coolingOff: coolingOffAllowed
    } = responsibleGamblingTools || {};
    const { active, showSelfDeleteInMyAccount } = accountDeactivation || {};
    const newLimitsAllowed = defaultLimits && newLimitsEnabled;
    const exclusionAllowed = selfExclusion && selfExclusionEnabled;
    const deactivationAllowed = active && selfDeletionEnabled;
    const deactivationAllowedInMyAccount = deactivationAllowed && showSelfDeleteInMyAccount;
    if (disabledPromotions) {
      cabinetMenu = s.entity.cabinetMenu.filter(
        (menu) => menu.title !== __("Default_._Title_._Active promotions")
      ).map((menu) => {
        const updatedMenu = { ...menu };
        updatedMenu.items = updatedMenu.items.filter(
          (section) => section.link !== allowedLinks.totoHistory
        );
        return updatedMenu;
      });
    }
    if (!((_a = platformSettings.casino) == null ? void 0 : _a.enabled)) {
      cabinetMenu = cabinetMenu.map((link) => {
        const updatedLinks = { ...link };
        updatedLinks.items = updatedLinks.items.filter(
          (item) => !excludedCasinoLinks.includes(item.link)
        );
        return updatedLinks;
      });
    }
    if (!platformSettings.showCasinoSessionHistory) {
      cabinetMenu = cabinetMenu.map((link) => {
        const updatedLinks = { ...link };
        updatedLinks.items = updatedLinks.items.filter(
          (item) => item.link !== allowedLinks.casinoSessionHistory
        );
        return updatedLinks;
      });
    }
    if (disableSportsBetting) {
      cabinetMenu = cabinetMenu.map((link) => {
        const updatedLinks = { ...link };
        updatedLinks.items = updatedLinks.items.filter(
          (item) => item.link !== allowedLinks.betHistory && item.link !== allowedLinks.totoHistory
        );
        return updatedLinks;
      });
    }
    cabinetMenu = cabinetMenu.map((link) => {
      const updatedLinks = { ...link };
      updatedLinks.items = updatedLinks.items.filter((item) => !newLimitsAllowed ? item.link !== allowedLinks.responsibleGamblingLimits : true).filter((item) => !exclusionAllowed ? item.link !== allowedLinks.responsibleGamblingExclusion : true).filter((item) => !coolingOffAllowed ? item.link !== allowedLinks.responsibleGamblingCoolingOff : true).filter((item) => !refProgramEnabled ? item.link !== allowedLinks.cabinetReferrals : true).filter((item) => {
        return !deactivationAllowed && item.link !== allowedLinks.responsibleGamblingDeactivation || deactivationAllowed && !deactivationAllowedInMyAccount && !item.isMyAccountBlock || deactivationAllowed && deactivationAllowedInMyAccount && !item.isOriginalBlock;
      });
      return updatedLinks;
    });
    const isLimitsDisabled = !limits ? true : !Object.values(limits).filter((limit) => limit).length;
    if (isLimitsDisabled) {
      cabinetMenu = cabinetMenu.map((link) => {
        const updatedLinks = { ...link };
        updatedLinks.items = updatedLinks.items.filter(
          (item) => !excludedLimits.includes(item.link)
        );
        return updatedLinks;
      });
    }
    if (!platformSettings.menuLimitsAvailable) {
      cabinetMenu = cabinetMenu.map((link) => {
        const updatedLinks = { ...link };
        updatedLinks.items = updatedLinks.items.filter(
          (item) => item.link !== allowedLinks.cabinetLimits
        );
        return updatedLinks;
      });
    }
    if (disabledVip || isBlockedVipFrontSettings) {
      cabinetMenu = cabinetMenu.map((link) => {
        const updatedLinks = { ...link };
        updatedLinks.items = updatedLinks.items.filter(
          (section) => section.link !== allowedLinks.cabinetCoins
        );
        return updatedLinks;
      });
    }
    if (!neccton) {
      cabinetMenu = cabinetMenu.map((link) => {
        const updatedLinks = { ...link };
        updatedLinks.items = updatedLinks.items.filter(
          (item) => item.link !== allowedLinks.reportsAndMessages
        );
        return updatedLinks;
      });
    }
    if (!newAnnualReport) {
      cabinetMenu = cabinetMenu.map((link) => {
        const updatedLinks = { ...link };
        updatedLinks.items = updatedLinks.items.filter(
          (item) => item.link !== allowedLinks.reports
        );
        return updatedLinks;
      });
    }
    if (typeof additionalCashierLink !== "undefined") {
      cabinetMenu = cabinetMenu.map((link) => {
        if (link.svg === "SvgCashier") {
          const updatedLinks = { ...link };
          updatedLinks.items.push({
            name: __("Default_._Title Or Link_._Another history"),
            link: additionalCashierLink,
            svg: SVGIcons.transaction,
            isBaseLink: true
          });
          return updatedLinks;
        }
        return link;
      });
    }
    return cabinetMenu;
  }
);
const selectLocaleDropdownCustomerMenu = createSelector(
  [
    getLocaleSetupState,
    isDisabledVip,
    selectisBlockedVip,
    isDisabledPromotions,
    selectIsReferralProgramEnabled,
    getPlatformSettingsState
  ],
  (s, isBlockedVip, isBlockedVipFrontSettings, isBlockedPromotions, referralProgramEnabled, platformSettings) => {
    const { dropDown } = s.entity;
    if (isBlockedPromotions || isBlockedVip || isBlockedVipFrontSettings || !referralProgramEnabled) {
      return dropDown.filter(
        (menu) => isBlockedPromotions ? menu.link !== allowedLinks.cabinetPromotion : true
      ).filter(
        (menu) => isBlockedVip || isBlockedVipFrontSettings ? menu.link !== allowedLinks.cabinetCoins : true
      ).filter(
        (menu) => !referralProgramEnabled ? menu.link !== allowedLinks.cabinetReferrals : true
      ).filter(
        (menu) => (platformSettings == null ? void 0 : platformSettings.disableSportsBetting) ? menu.link !== allowedLinks.betHistory : true
      );
    }
    return dropDown;
  }
);
const fetchCustomerDataEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCustomerStart.type),
  exhaustMap(() => {
    return Api.customer.getCustomerData().pipe(
      map((data) => getCustomerSuccess(data.user)),
      catchError((err) => of(getCustomerFailure(err))),
      shareReplay({ refCount: true })
    );
  })
);
const fetchCustomerLastBetEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCustomerLastBetStart.type),
  exhaustMap(() => Api.customer.getCustomerLastBetSum().pipe(
    map((data) => getCustomerLastBetSuccess(data)),
    catchError((err) => of(getCustomerLastBetFailure(err)))
  ))
);
const fetchCustomerFastAmountsEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCustomerFastAmountsStart.type),
  exhaustMap(() => Api.customer.getCustomerFastAmounts().pipe(
    map((data) => getCustomerFastAmountsSuccess(data)),
    catchError((err) => of(getCustomerFastAmountsFailure(err)))
  ))
);
const startTrialBroadcast = () => ({
  type: getBroadcastStart.type
});
const customerStartTrialBroadcastEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBroadcastStart.type),
  switchMap(() => Api.customer.getBroadcastStart().pipe(
    map((data) => getBroadcastStartSuccess(data)),
    catchError((err) => of(getBroadcastStartFailure(err)))
  ))
);
const stopTrialBroadcast = () => ({
  type: getBroadcastStop.type
});
const customerStopTrialBroadcastEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getBroadcastStop.type),
  switchMap(() => Api.customer.getBroadcastStop().pipe(
    map((data) => getBroadcastStopSuccess(data)),
    catchError((err) => of(getBroadcastStopFailure(err)))
  ))
);
const changePassword = (formData) => ({
  type: changePasswordStart.type,
  payload: formData
});
const changePasswordEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(changePasswordStart.type),
  switchMap((action) => Api.customer.changeCustomerPassword(action.payload).pipe(
    map((data) => changePasswordSuccess(data)),
    catchError((err) => of(changePasswordFailed(err)))
  ))
);
const customerSaveDataEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(customerUpdateStart.type),
  switchMap(
    (action) => Api.customer.saveCustomer(action.payload.customer).pipe(
      map((data) => customerUpdateSuccess({
        data,
        allowSuccessSnackbar: action.payload.allowSuccessSnackbar,
        noNotification: action.payload.noNotification
      })),
      catchError((err) => of(customerUpdateFail(err)))
    )
  )
);
const updateMigratedCustomerData = (customer) => ({
  type: updateMigratedCustomerStart.type,
  payload: {
    customer
  }
});
const updateMigratedCustomerDataEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(updateMigratedCustomerStart.type),
  switchMap(
    (action) => Api.customer.updateMigratedCustomerData(action.payload.customer).pipe(
      map((data) => updateMigratedCustomerSuccess({
        data
      })),
      catchError((err) => of(updateMigratedCustomerError(err)))
    )
  )
);
const updateCustomerIssuranceStatusEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(changeBonusIssuanceStart.type),
  switchMap(
    (action) => Api.customer.updateCustomerIssuranceStatus(action.payload).pipe(
      map(() => changeBonusIssuanceSuccess(action.payload)),
      catchError(() => of(changeBonusIssuanceFail()))
    )
  )
);
const toggleCustumerBonusInCashier = (isBonusOnInCashier) => {
  return { type: toggleBonusInCashierStart.type, payload: isBonusOnInCashier };
};
const toggleCustumerBonusInCashierEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(toggleBonusInCashierStart.type),
  switchMap(
    (action) => Api.customer.toggleBonusInCashier(action.payload).pipe(
      map(() => toggleBonusInCashierSuccess(action.payload)),
      catchError(() => of(toggleBonusInCashierFail()))
    )
  )
);
const getCustomerWSTokenEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(customerWSTokenStart.type),
  switchMap(() => Api.customer.getCustomerWSToken().pipe(
    map((data) => customerWSTokenSuccess(data)),
    catchError(() => of(customerWSTokenFail()))
  ))
);
const openCustomerWS = (data) => ({
  type: customerWSOpenStart.type,
  payload: data
});
const openCustomerWSEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(customerWSOpenStart.type),
  mergeMap((action) => {
    var _a;
    return fromWebSocket(
      {
        namespace: WSNamespaces.users,
        payload: {
          token: action.payload,
          customerId: selectCustomerId(state$.value),
          customerToken: (_a = selectCustomerTokenEntities(state$.value)) == null ? void 0 : _a.token
        },
        api: Api.wsPrivateClient
      },
      void 0,
      {
        isProtoBuf: selectGrpcIsActive(state$.value)
      }
    ).pipe(
      map((data) => {
        data.type = data.event;
        return data;
      }),
      map((data) => customerSocketOpenSuccess(data)),
      takeUntil(action$.pipe(ofType(clearCustomerData.type))),
      catchError((err) => of(customerSocketError(err)))
    );
  })
);
const searchCustomerDataByPersonalCode = (data) => ({
  type: customerPersonalCheckStart.type,
  payload: data
});
const searchCustomerDataByPersonalCodeEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(customerPersonalCheckStart.type),
  switchMap((action) => from(
    Api.customer.checkPersonalCode(action.payload)
  ).pipe(
    map((checkEntity) => customerPersonalCheckSuccess(checkEntity)),
    catchError((reason) => of(customerPersonalCheckFailure(reason)))
  ))
);
const getCustomerGamblingAccountInfo = () => ({
  type: getCustomerGamblingInfoStart.type
});
const getCustomerGamblingAccountInfoEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCustomerGamblingInfoStart.type),
  switchMap(() => Api.customerv2.getGamblingAccountInfo().pipe(
    pluck("item"),
    map((checkEntity) => getCustomerGamblingInfoSuccess(checkEntity)),
    catchError(() => of(getCustomerGamblingInfoFail()))
  ))
);
const changeBalanceAccount = (params) => ({
  type: changeBalanceAccountStart.type,
  payload: params
});
const changeBalanceAccountEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(changeBalanceAccountStart.type),
  switchMap((action) => Api.customer.setBalanceAccount(action.payload).pipe(
    map(() => changeBalanceAccountSuccess()),
    catchError(() => of(changeBalanceAccountError()))
  ))
);
const createBalanceAccountEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(createBalanceAccountStart.type),
  switchMap(({ payload }) => Api.customer.createBalanceAccount(
    payload.currencyCode
  ).pipe(
    mergeMap((res) => payload.change ? of(
      createBalanceAccountSuccess(),
      changeBalanceAccount({ userId: res.user_id, accountId: res.id })
    ) : of(createBalanceAccountSuccess())),
    catchError(() => of(createBalanceAccountError()))
  ))
);
const createBalanceAccount = (payload) => ({
  type: createBalanceAccountStart.type,
  payload
});
const changeMainCurrencyEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(changeMainCurrencyStart.type),
  switchMap(({ payload }) => Api.customer.changeMainCurrency(
    payload
  ).pipe(
    map(() => changeBalanceAccountSuccess()),
    catchError(() => of(changeBalanceAccountError()))
  ))
);
const changeMainCurrency = (payload) => ({
  type: changeMainCurrencyStart.type,
  payload
});
const subscribeLastBetUpdateWs = () => ({
  type: subscribeLastBetUpdateWsStart.type
});
const openLastBetUpdateEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(subscribeLastBetUpdateWsStart.type),
  mergeMap((action) => fromWebSocket(
    {
      namespace: WSNamespaces.lastBetSumUpdate,
      payload: {
        customerId: selectCustomerId(state$.value)
      },
      api: Api.wsPrivateClient
    },
    void 0,
    {
      isProtoBuf: selectGrpcIsActive(state$.value)
    }
  ).pipe(
    map((data) => {
      data.type = data.event;
      return data;
    }),
    map((data) => lastBetUpdateSocketOpenSuccess(data)),
    takeUntil(action$.pipe(ofType(clearCustomerData.type))),
    catchError((err) => of(customerSocketError(err)))
  ))
);
const getWatchersChatConfig = () => ({
  type: watchersChatStart.type
});
const getWatchersChatConfigEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(watchersChatStart.type),
  switchMap((action) => Api.customer.getWatchersChatInfo(action.payload).pipe(
    map((data) => watchersChatSuccess(data)),
    catchError(() => of(watchersChatFail()))
  ))
);
const getUserAccountSmarticoHashEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(updateUserAccountSmarticoHashStart.type),
  switchMap(() => Api.customer.getUserSmarticoHash().pipe(
    map((data) => updateUserAccountSmarticoHashSuccess(data)),
    catchError(() => of(updateUserAccountSmarticoHashError()))
  ))
);
export {
  ReactivationSent,
  allowBroadcast,
  changeBalanceAccount,
  changeBalanceAccountEpic,
  changeBalanceAccountError,
  changeBalanceAccountStart,
  changeBonusIssuanceFail,
  changeBonusIssuanceSuccess,
  changeMainCurrency,
  changeMainCurrencyEpic,
  changeMainCurrencyStart,
  changeOntarioMigrationNoticeState,
  changePassword,
  changePasswordEpic,
  changePasswordFailed,
  changePasswordStart,
  changePasswordSuccess,
  clearGviveMigrationCompletion,
  clearPasswordLoadedState,
  createBalanceAccount,
  createBalanceAccountEpic,
  createBalanceAccountError,
  createBalanceAccountStart,
  createBalanceAccountSuccess,
  createYearList,
  customerPersonalCheckFailure,
  customerPersonalCheckStart,
  customerPersonalCheckSuccess,
  customerReducer,
  customerSaveDataEpic,
  customerSlice,
  customerSocketError,
  customerStartTrialBroadcastEpic,
  customerStopTrialBroadcastEpic,
  customerUpdateFail,
  customerWSOpenStart,
  customerWSTokenFail,
  customerWSTokenSuccess,
  fetchCustomerDataEpic,
  fetchCustomerFastAmountsEpic,
  fetchCustomerLastBetEpic,
  getBroadcastStart,
  getBroadcastStartFailure,
  getBroadcastStartSuccess,
  getBroadcastStop,
  getBroadcastStopFailure,
  getBroadcastStopSuccess,
  getCustomerFailure,
  getCustomerFastAmountsFailure,
  getCustomerFastAmountsSuccess,
  getCustomerFinalizeRegStep,
  getCustomerGamblingAccountInfo,
  getCustomerGamblingAccountInfoEpic,
  getCustomerGamblingInfoFail,
  getCustomerGamblingInfoStart,
  getCustomerGamblingInfoSuccess,
  getCustomerLastBetFailure,
  getCustomerLastBetSuccess,
  getCustomerSuccess,
  getCustomerWSTokenEpic,
  getForceUserLimitsSetup,
  getUserAccountSmarticoHashEpic,
  getWatchersChatConfig,
  getWatchersChatConfigEpic,
  hideCustomerLimitsSetupPopup,
  initialCustomerState,
  lastBetUpdateSocketOpenSuccess,
  openCustomerWS,
  openCustomerWSEpic,
  openLastBetUpdateEpic,
  searchCustomerDataByPersonalCode,
  searchCustomerDataByPersonalCodeEpic,
  selectAccountsForDropDown,
  selectBalanceAccountCurrentlyChanging,
  selectCustomerAccounts,
  selectCustomerBalanceLimitState,
  selectCustomerBonusOnInCashier,
  selectCustomerCompPointsCasino,
  selectCustomerCustomerHash,
  selectCustomerDeductibleStake,
  selectCustomerEntitiesBonusType,
  selectCustomerError,
  selectCustomerGamblingInfo,
  selectCustomerGroups,
  selectCustomerLoaded,
  selectCustomerLoadedOnce,
  selectCustomerOndatoVerificationNoticeStatus,
  selectCustomerPasswordLoadedStatus,
  selectCustomerPersonalCodeCheck,
  selectCustomerSessionLimitState,
  selectCustomerToken,
  selectCustomerUpdateLoadingState,
  selectCustomerVerificationOndatoStatus,
  selectCustomerVipNotAllowed,
  selectCustomerWsToken,
  selectIsCustomerGviveVerified,
  selectIsCustomerYoungAdult,
  selectIsGviveMigrationCompleted,
  selectIsOndatoVerificationRequired,
  selectIsOndatoVerifiedCustomer,
  selectIsPgwWidget,
  selectLocaleDropdownCustomerMenu,
  selectLocaleSetupCabinetMenu,
  selectOntarioMigrationNoticeState,
  selectResponsibleGamblingToolsActive,
  selectResponsibleGamblingToolsError,
  selectWatchersChatPlatformConfig,
  setResponsibleGamblingToolsActive,
  setResponsibleGamblingToolsError,
  setWebsocketHandledNotifications,
  showVerificationPageAfterRegistration,
  startTrialBroadcast,
  stopBroadcastTrialWS,
  stopTrialBroadcast,
  subscribeLastBetUpdateWs,
  subscribeLastBetUpdateWsStart,
  toggleBonusInCashierFail,
  toggleBonusInCashierStart,
  toggleBonusInCashierSuccess,
  toggleCustumerBonusInCashier,
  toggleCustumerBonusInCashierEpic,
  updateCustomerAccount,
  updateCustomerCompPoints,
  updateCustomerIssuranceStatusEpic,
  updateCustomerLimitsHit,
  updateCustomerState,
  updateGBGUserResult,
  updateLastSumBet,
  updateMigratedCustomerData,
  updateMigratedCustomerDataEpic,
  updateMigratedCustomerError,
  updateMigratedCustomerStart,
  updateMigratedCustomerSuccess,
  updateUserAccountSmarticoHashError,
  updateUserAccountSmarticoHashSuccess,
  watchersChatFail,
  watchersChatStart,
  watchersChatSuccess
};
